export type ServiceRequestTransferRole = "MANAGER" | "CASHIER" | "WAITER";

export interface ServiceRequestTransferFrozenCommand {
  eventId: string;
  expectedWorkItemVersion: number;
  targetRole: ServiceRequestTransferRole;
  targetCandidateId: string | null;
  targetDisplayName: string;
  reason: string;
  idempotencyKey: string;
}

export type ServiceRequestTransferRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface ServiceRequestTransferRecovery {
  ownerIdentityKey: string;
  command: ServiceRequestTransferFrozenCommand;
  state: ServiceRequestTransferRecoveryState;
}

export type ServiceRequestTransferRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: ServiceRequestTransferRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE" };

interface StorageAccess {
  browser: boolean;
  storage: Storage | null;
}

const definitelyNoEffectAfterIdempotencyLookup = new Set([
  "IDEMPOTENCY_COLLISION",
  "SERVICE_REQUEST_ASSIGNEE_MISMATCH",
  "SERVICE_REQUEST_FINALIZED",
  "SERVICE_REQUEST_NOT_ACKNOWLEDGED",
  "SERVICE_REQUEST_NOT_FOUND",
  "SERVICE_REQUEST_TRANSFER_TARGET_UNAVAILABLE",
  "SERVICE_REQUEST_TRANSFER_VERSION_CONFLICT",
]);

const definitelyNoEffectForClaimedAttempt = new Set([
  ...definitelyNoEffectAfterIdempotencyLookup,
  "AUTH_REQUIRED",
  "AUTH_CREDENTIAL_CONFLICT",
  "AUTHORIZATION_DEFAULT_DENY",
  "CSRF_REJECTED",
  "FORBIDDEN",
  "MOBILE_SESSION_ACTIVATION_REQUIRED",
  "NATIVE_APP_VERSION_INVALID",
  "NATIVE_APP_VERSION_MISMATCH",
  "NATIVE_APP_VERSION_REQUIRED",
  "NATIVE_SECURE_SESSION_UNAVAILABLE",
  "NATIVE_SERVER_UPDATE_REQUIRED",
  "NATIVE_UPDATE_REQUIRED",
  "ORIGIN_REJECTED",
  "REQUEST_VALIDATION_FAILED",
]);

export const SERVICE_REQUEST_TRANSFER_RECOVERY_STORAGE_KEY =
  "billiard-erp:service-request-transfer-recovery:v1";

let pendingRecovery: ServiceRequestTransferRecovery | null = null;
let dispatchingRecovery: ServiceRequestTransferRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;

export function serviceRequestTransferIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isBoundedTrimmedString(value: unknown, minimum: number, maximum: number): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    [...value].length >= minimum &&
    [...value].length <= maximum
  );
}

function isIdentityKey(value: unknown): value is string {
  if (!isBoundedTrimmedString(value, 1, 1_024)) return false;
  try {
    const identity = JSON.parse(value) as unknown;
    return (
      Array.isArray(identity) &&
      identity.length === 3 &&
      identity.every((part) => isBoundedTrimmedString(part, 1, 256))
    );
  } catch {
    return false;
  }
}

function isTransferRole(value: unknown): value is ServiceRequestTransferRole {
  return value === "MANAGER" || value === "CASHIER" || value === "WAITER";
}

function isFrozenCommand(value: unknown): value is ServiceRequestTransferFrozenCommand {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "eventId",
      "expectedWorkItemVersion",
      "targetRole",
      "targetCandidateId",
      "targetDisplayName",
      "reason",
      "idempotencyKey",
    ])
  ) {
    return false;
  }
  return (
    isBoundedTrimmedString(value.eventId, 1, 256) &&
    Number.isSafeInteger(value.expectedWorkItemVersion) &&
    Number(value.expectedWorkItemVersion) >= 1 &&
    isTransferRole(value.targetRole) &&
    (value.targetCandidateId === null || isBoundedTrimmedString(value.targetCandidateId, 1, 256)) &&
    isBoundedTrimmedString(value.targetDisplayName, 1, 80) &&
    isBoundedTrimmedString(value.reason, 2, 300) &&
    isBoundedTrimmedString(value.idempotencyKey, 8, 128)
  );
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: ServiceRequestTransferFrozenCommand,
  state: ServiceRequestTransferRecoveryState,
): ServiceRequestTransferRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: Object.freeze(structuredClone(command)),
    state,
  });
}

function storedRecovery(value: unknown): ServiceRequestTransferRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery)
  ) {
    return null;
  }
  const recovery = value.recovery;
  if (
    !hasExactKeys(recovery, ["ownerIdentityKey", "command", "state"]) ||
    !isIdentityKey(recovery.ownerIdentityKey) ||
    !isFrozenCommand(recovery.command) ||
    (recovery.state !== "CLAIMED" && recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableRecovery(recovery.ownerIdentityKey, recovery.command, recovery.state);
}

function recoveryEquals(
  left: ServiceRequestTransferRecovery,
  right: ServiceRequestTransferRecovery,
): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    JSON.stringify(left.command) === JSON.stringify(right.command)
  );
}

function sessionStorageAccess(): StorageAccess {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function readStoredRecovery(storage: Storage): ServiceRequestTransferRecovery | null {
  const encoded = storage.getItem(SERVICE_REQUEST_TRANSFER_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStoredRecovery(recovery: ServiceRequestTransferRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    access.storage.setItem(
      SERVICE_REQUEST_TRANSFER_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function verifyStoredRecovery(recovery: ServiceRequestTransferRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function hydrateStoredRecovery(): void {
  if (hydrated) return;
  hydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(SERVICE_REQUEST_TRANSFER_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    const stored = readStoredRecovery(access.storage);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovery =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStoredRecovery(recovery)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = recovery;
  } catch {
    recoveryUnavailable = true;
  }
}

export function readServiceRequestTransferRecovery(
  identityKey: string,
): ServiceRequestTransferRecoveryView {
  hydrateStoredRecovery();
  if (recoveryUnavailable) return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  if (!pendingRecovery) return { status: "NONE" };
  if (pendingRecovery.ownerIdentityKey !== identityKey) return { status: "OTHER_OPERATOR" };
  return { status: "OWNED", recovery: pendingRecovery };
}

export function claimServiceRequestTransferRecovery(
  identityKey: string,
  command: ServiceRequestTransferFrozenCommand,
): ServiceRequestTransferRecoveryView {
  const current = readServiceRequestTransferRecovery(identityKey);
  if (current.status !== "NONE") return current;
  if (!isIdentityKey(identityKey) || !isFrozenCommand(command)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const recovery = immutableRecovery(identityKey, command, "CLAIMED");
  if (!saveStoredRecovery(recovery)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  pendingRecovery = recovery;
  return { status: "OWNED", recovery };
}

function verifyRecoveryForDispatch(
  recovery: ServiceRequestTransferRecovery,
  identityKey: string,
): boolean {
  hydrateStoredRecovery();
  if (
    recoveryUnavailable ||
    !pendingRecovery ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey ||
    !verifyStoredRecovery(recovery)
  ) {
    recoveryUnavailable = true;
    return false;
  }
  return true;
}

export function beginServiceRequestTransferDispatch(
  recovery: ServiceRequestTransferRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery || !verifyRecoveryForDispatch(recovery, identityKey)) return false;
  dispatchingRecovery = recovery;
  return true;
}

export function finishServiceRequestTransferDispatch(
  recovery: ServiceRequestTransferRecovery,
  identityKey: string,
): boolean {
  if (dispatchingRecovery !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatchingRecovery = null;
  return true;
}

export function markServiceRequestTransferOutcomeUnknown(
  recovery: ServiceRequestTransferRecovery,
  identityKey: string,
): boolean {
  if (!verifyRecoveryForDispatch(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStoredRecovery(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  return true;
}

export function clearServiceRequestTransferRecovery(
  recovery: ServiceRequestTransferRecovery,
  identityKey: string,
): boolean {
  if (
    recoveryUnavailable ||
    !pendingRecovery ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  if (access.browser) {
    if (!access.storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      const stored = readStoredRecovery(access.storage);
      if (!stored || !recoveryEquals(stored, recovery)) {
        recoveryUnavailable = true;
        return false;
      }
      access.storage.removeItem(SERVICE_REQUEST_TRANSFER_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(SERVICE_REQUEST_TRANSFER_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
        return false;
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  pendingRecovery = null;
  return true;
}

function isRecordWithCode(error: unknown): error is { code: string } {
  return isRecord(error) && typeof error.code === "string";
}

export function serviceRequestTransferOutcomeIsUnknown(
  error: unknown,
  state: ServiceRequestTransferRecoveryState,
): boolean {
  if (!isRecordWithCode(error)) return true;
  return !(state === "CLAIMED"
    ? definitelyNoEffectForClaimedAttempt.has(error.code)
    : definitelyNoEffectAfterIdempotencyLookup.has(error.code));
}

export function resetServiceRequestTransferRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  const access = sessionStorageAccess();
  try {
    access.storage?.removeItem(SERVICE_REQUEST_TRANSFER_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup also resets the in-memory fail-closed state.
  }
}

export function resetServiceRequestTransferRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
}
